import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import '../styles/LoginScreen.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const KNOWN_DOMAINS = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com'];

function getDomainSuggestions(email) {
  const atIdx = email.indexOf('@');
  if (atIdx < 1) return [];
  const afterAt = email.slice(atIdx + 1).toLowerCase();
  if (!afterAt) return KNOWN_DOMAINS;
  return KNOWN_DOMAINS.filter(d => d.startsWith(afterAt));
}

async function callEdgeFunction(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

const EyeOpen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9.88 9.88a3 3 0 104.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const ErrorBox = ({ message }) => (
  <div className="login-error">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <span>{message}</span>
  </div>
);

const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.pathname === '/standouts/signup' ? 'signup' : 'login'
  );

  // ── Login state ──────────────────────────────────────────
  const [loginEmail, setLoginEmail] = useState('');
  const [loginEmailDomains, setLoginEmailDomains] = useState([]);
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPhase1Modal, setShowPhase1Modal] = useState(false);

  // ── Signup state ─────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupEmailDomains, setSignupEmailDomains] = useState([]);
  const [signupPassword, setSignupPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('coach');
  const [roleOpen, setRoleOpen] = useState(false);
  const roleRef = useRef(null);
  const [showSignupPw, setShowSignupPw] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');

  useEffect(() => {
    const handler = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!loginEmail || !loginPassword) {
      setLoginError('Please enter both email and password');
      return;
    }
    setLoginLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword.trim(),
      });
      if (signInError) throw signInError;
      if (!data.user) throw new Error('Login failed');

      // Check user's role from profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('role, email_verified')
        .eq('id', data.user.id)
        .single();

      if (profileError) throw new Error('Failed to retrieve user profile');

      // First gate: email must be OTP-verified. If not, resend a code and send
      // them to the OTP screen (session stays active so they can verify there).
      if (!profileData.email_verified) {
        await callEdgeFunction('send-otp', {
          email: loginEmail.trim(),
          purpose: 'email_verification',
        });
        navigate('/standouts/verify-email', { state: { email: loginEmail.trim() } });
        return;
      }

      // Define Phase 0 and Phase 1 roles
      const phase0Roles = ['coach', 'trainer', 'league_admin'];
      const phase1Roles = ['admin', 'fan', 'parent', 'athlete', 'statkeeper'];

      // Check if user has a Phase 1 role
      if (phase1Roles.includes(profileData.role)) {
        // Sign out the user immediately
        await supabase.auth.signOut();
        // Show Phase 1 modal
        setShowPhase1Modal(true);
        return;
      }

      // Check if user has a valid Phase 0 role
      if (!phase0Roles.includes(profileData.role)) {
        await supabase.auth.signOut();
        throw new Error('Invalid role. Please contact support.');
      }

      const { data: approvalData, error: approvalError } = await supabase
        .rpc('check_user_approval', { p_user_id: data.user.id });
      if (approvalError) throw new Error('Failed to verify account status');
      if (!approvalData || approvalData.length === 0)
        throw new Error('Account not found. Please sign up first.');

      if (!approvalData[0].approved) {
        navigate('/standouts/pending-approval');
        return;
      }
      // Approved but hasn't seen the one-time "Account Approved" screen yet
      if (!approvalData[0].acknowledged) {
        navigate('/standouts/account-approved');
        return;
      }
      navigate('/standouts/submit');
    } catch (err) {
      setLoginError(err.message || 'Invalid email or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    if (!firstName.trim() || !lastName.trim() || !signupEmail.trim() || !signupPassword.trim() || !phone.trim()) {
      setSignupError('Unable to create account. Please complete all required information and try again. If the issue continues, contact support.');
      return;
    }
    setSignupLoading(true);
    try {
      // Moderation gate: block profane/inappropriate names before creating the
      // account. Uses the shared moderate-content edge function (Phase-0 hits the
      // same Supabase project / banned_words list as Phase-1).
      const moderation = await callEdgeFunction('moderate-content', {
        type: 'text',
        content: `${firstName.trim()} ${lastName.trim()}`,
        context: 'name',
      });
      if (moderation?.action === 'block') {
        setSignupError("Please choose a different name. The name entered contains language that isn't allowed on LegacyStat.");
        return;
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: signupEmail.trim(),
        password: signupPassword.trim(),
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
          },
        },
      });
      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Failed to create user account');

      const userId = authData.user.id;
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert([{
          id: userId,
          email: signupEmail.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          role,
        }], { onConflict: 'id' });
      if (profileError) throw new Error('Failed to update profile. Please contact support.');

      const roleTable = role === 'trainer' ? 'trainers' : role === 'league_admin' ? 'league_admins' : 'coaches';
      const { error: roleError } = await supabase
        .from(roleTable)
        .insert([{ user_id: userId, phone: phone.trim(), approved: false, onboarding_completed: true }]);
      if (roleError) throw new Error('Failed to complete registration. Please contact support.');

      await callEdgeFunction('send-otp', {
        email: signupEmail.trim(),
        purpose: 'email_verification',
      });

      navigate('/standouts/verify-email', { state: { email: signupEmail.trim() } });
    } catch (err) {
      if (err.message?.includes('rate limit')) {
        setSignupError('Too many signup attempts. Please wait a few minutes and try again.');
      } else if (err.message?.includes('already registered') || err.message?.includes('User already registered')) {
        setSignupError('This email is already registered. Please sign in instead.');
      } else {
        setSignupError('Unable to create account. Please complete all required information and try again. If the issue continues, contact support.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <button
        className="back-btn"
        onClick={() => navigate('/standouts')}
        style={{ position: 'absolute', top: '24px', left: '24px', zIndex: 10 }}
      >
        ← Back to Standouts
      </button>

      <div className="login-container signup-container">

        {/* ── LOGIN FORM ── */}
        {activeTab === 'login' && (
          <>
            <div className="login-header">
              <div className="login-logo-wrapper">
                <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
              </div>
              <h1 className="login-title">Welcome Back</h1>
              <p className="login-subtitle">Sign in to continue to LegacyStat</p>
            </div>

            {/* Tab switcher */}
            <div className="auth-tab-pills">
              <button type="button" className={`auth-tab-pill ${activeTab === 'login' ? 'auth-tab-pill-active' : ''}`} onClick={() => setActiveTab('login')}>Sign In</button>
              <button type="button" className={`auth-tab-pill ${activeTab === 'signup' ? 'auth-tab-pill-active' : ''}`} onClick={() => setActiveTab('signup')}>Create Account</button>
            </div>

            <form className="login-form" onSubmit={handleLogin}>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Email Address</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter your email"
                  value={loginEmail}
                  onChange={(e) => { setLoginEmail(e.target.value); setLoginEmailDomains(getDomainSuggestions(e.target.value)); }}
                  onBlur={() => setTimeout(() => setLoginEmailDomains([]), 150)}
                  autoComplete="off"
                  required
                />
                {loginEmailDomains.length > 0 && (
                  <div className="email-domain-dropdown">
                    {loginEmailDomains.map(domain => {
                      const local = loginEmail.slice(0, loginEmail.indexOf('@') + 1);
                      return (
                        <button
                          key={domain}
                          type="button"
                          className="email-domain-option"
                          onMouseDown={(e) => { e.preventDefault(); setLoginEmail(`${local}${domain}`); setLoginEmailDomains([]); }}
                        >
                          {local}<strong>{domain}</strong>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showLoginPw ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowLoginPw(v => !v)} tabIndex="-1">
                    {showLoginPw ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <button type="button" className="forgot-password-link" onClick={() => navigate('/standouts/forgot-password')}>
                    Forgot password?
                  </button>
                </div>
              </div>

              {loginError && <ErrorBox message={loginError} />}

              <button type="submit" className="login-button" disabled={loginLoading}>
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="login-forgot-password">
                <button type="button" className="forgot-password-link" onClick={() => setActiveTab('signup')}>
                  Don't have an account? Sign Up
                </button>
              </div>
            </form>
          </>
        )}

        {/* ── SIGNUP FORM ── */}
        {activeTab === 'signup' && (
          <>
            <div className="login-header">
              <div className="login-logo-wrapper">
                <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
              </div>
              <h1 className="login-title">Create Account</h1>
              <p className="login-subtitle">Join as a coach, trainer, or league admin</p>
            </div>

            {/* Tab switcher */}
            <div className="auth-tab-pills">
              <button type="button" className={`auth-tab-pill ${activeTab === 'login' ? 'auth-tab-pill-active' : ''}`} onClick={() => setActiveTab('login')}>Sign In</button>
              <button type="button" className={`auth-tab-pill ${activeTab === 'signup' ? 'auth-tab-pill-active' : ''}`} onClick={() => setActiveTab('signup')}>Create Account</button>
            </div>

            <form className="login-form" onSubmit={handleSignup}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name <span style={{ color: '#D8A134' }}>*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name <span style={{ color: '#D8A134' }}>*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Email Address <span style={{ color: '#D8A134' }}>*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter your email"
                  value={signupEmail}
                  onChange={(e) => { setSignupEmail(e.target.value); setSignupEmailDomains(getDomainSuggestions(e.target.value)); }}
                  onBlur={() => setTimeout(() => setSignupEmailDomains([]), 150)}
                  autoComplete="off"
                  required
                />
                {signupEmailDomains.length > 0 && (
                  <div className="email-domain-dropdown">
                    {signupEmailDomains.map(domain => {
                      const local = signupEmail.slice(0, signupEmail.indexOf('@') + 1);
                      return (
                        <button
                          key={domain}
                          type="button"
                          className="email-domain-option"
                          onMouseDown={(e) => { e.preventDefault(); setSignupEmail(`${local}${domain}`); setSignupEmailDomains([]); }}
                        >
                          {local}<strong>{domain}</strong>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Password <span style={{ color: '#D8A134' }}>*</span></label>
                <div className="password-input-wrapper">
                  <input
                    type={showSignupPw ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Create a password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowSignupPw(v => !v)} tabIndex="-1">
                    {showSignupPw ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone Number <span style={{ color: '#D8A134' }}>*</span></label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group" style={{ position: 'relative' }} ref={roleRef}>
                  <label className="form-label">Role <span style={{ color: '#D8A134' }}>*</span></label>
                  <div
                    onClick={() => setRoleOpen(o => !o)}
                    style={{
                      width: '100%', padding: '12px 40px 12px 12px',
                      background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${roleOpen ? '#D8A134' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '12px', color: '#fff', fontSize: '16px', cursor: 'pointer',
                      boxSizing: 'border-box', userSelect: 'none', position: 'relative',
                      boxShadow: roleOpen ? '0 0 0 3px rgba(216,161,52,0.1)' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {{ coach: 'Coach', trainer: 'Trainer', league_admin: 'League Admin' }[role]}
                    <svg
                      style={{
                        position: 'absolute', right: '14px', top: '50%',
                        transform: `translateY(-50%) rotate(${roleOpen ? 180 : 0}deg)`,
                        transition: 'transform 0.2s', pointerEvents: 'none',
                      }}
                      width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="#f3b129" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {roleOpen && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                      background: '#0e0e0e', borderRadius: '12px', zIndex: 50, overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                    }}>
                      {[
                        { value: 'coach', label: 'Coach' },
                        { value: 'trainer', label: 'Trainer' },
                        { value: 'league_admin', label: 'League Admin' },
                      ].map((opt) => (
                        <div
                          key={opt.value}
                          onClick={() => { setRole(opt.value); setRoleOpen(false); }}
                          style={{
                            padding: '11px 14px', fontSize: '15px', cursor: 'pointer',
                            color: role === opt.value ? '#f3b129' : '#fff',
                            background: role === opt.value ? 'rgba(243,177,41,0.08)' : 'transparent',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => { if (role !== opt.value) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                          onMouseLeave={(e) => { if (role !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {signupError && <ErrorBox message={signupError} />}

              <button type="submit" className="login-button" disabled={signupLoading}>
                {signupLoading ? 'Creating account...' : 'Create Account'}
              </button>

              <div className="login-forgot-password">
                <button type="button" className="forgot-password-link" onClick={() => setActiveTab('login')}>
                  Already have an account? Sign In
                </button>
              </div>
            </form>
          </>
        )}

        <div className="login-footer">
          <p className="footer-text">© {new Date().getFullYear()} LegacyStat. All rights reserved.</p>
        </div>

      </div>

      <div className="login-background-glow"></div>
      <div className="login-background-pattern"></div>

      {/* Phase 1 Role Modal */}
      {showPhase1Modal && (
        <div className="modal-overlay" onClick={() => setShowPhase1Modal(false)}>
          <div className="phase1-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D8A134" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 className="modal-title">Access Restricted</h2>
            <p className="modal-message">
              This account role is not available in Phase 0. Your role will be activated in Phase 1 of our platform launch.
            </p>
            <p className="modal-submessage">
              Phase 0 is currently limited to <strong>Coaches</strong>, <strong>Trainers</strong>, and <strong>League Admins</strong> only.
            </p>
            <button className="modal-close-btn" onClick={() => setShowPhase1Modal(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
